import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Descriptions,
  Tag,
  Button,
  Spin,
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Alert,
  Badge,
  Progress,
  Empty,
  Table,
  Segmented,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHelp from '../../components/PageHelp';
import {
  getTerminal,
  getTerminalTelemetry,
  getTerminalAlerts,
  getTerminalUsage,
  type Terminal,
  type ServiceLine,
  type LiveTelemetry,
  type DeviceAlertEntry,
  type TerminalUsage,
  type DailyDataUsage,
} from '../../services/clarusService';
import { fmtThroughput } from '../../utils/telemetryFormat';
import TelemetryHistoryChart from '../../components/TelemetryHistoryChart';
import AutoRefreshControl from '../../components/AutoRefreshControl';
import UsageChart, { fmtDay } from '../../components/UsageChart';
import {
  dailyUsageOf,
  lastNDays,
  needsPreviousCycle,
  previousBillingPeriod,
  hasAllowance,
  isOverageBilled,
  type UsageWindow,
} from '../../utils/usageWindow';

const fmtUptime = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const fmtPercent = (v: number | null | undefined, digits = 2): string =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(digits)}%`;

const fmtDate = (v: string | null | undefined): string => (v ? new Date(v).toLocaleString() : '—');

const fmtGB = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : `${v.toFixed(2)} GB`;

const TerminalDetail: React.FC = () => {
  const { deviceId = '' } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();

  const [terminal, setTerminal] = useState<Terminal | null>(null);
  const [serviceLine, setServiceLine] = useState<ServiceLine | null>(null);
  const [telemetry, setTelemetry] = useState<LiveTelemetry | null>(null);
  const [alerts, setAlerts] = useState<DeviceAlertEntry[]>([]);
  const [usage, setUsage] = useState<TerminalUsage | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Live sections degrade independently: a Clarus outage should still leave the
  // stored terminal record on screen rather than blanking the whole page.
  const [liveError, setLiveError] = useState<string | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // KAN-111: one refresh drives every section, the chart included.
  const [refreshToken, setRefreshToken] = useState(0);
  const [autoRefreshMs, setAutoRefreshMs] = useState(0);
  // Live sections load behind their own indicators so the page is not held
  // hostage to Clarus latency.
  const [liveLoading, setLiveLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [previousDays, setPreviousDays] = useState<DailyDataUsage[]>([]);
  const [usageWindow, setUsageWindow] = useState<UsageWindow>('last30');

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setError(null);
      setLiveError(null);
      setUsageError(null);
      setLiveLoading(true);
      setUsageLoading(true);
      try {
        // The stored record comes from our own database and returns in
        // milliseconds. Everything below proxies to Clarus, where the calls
        // take 0.5-3.3s each. Awaiting them here kept the whole page behind a
        // spinner for seconds; instead the page renders on this, and each live
        // section fills in behind its own indicator.
        const detail = await getTerminal(deviceId, signal);
        if (signal?.aborted) return;
        setTerminal(detail.terminal);
        setServiceLine(detail.serviceLine);
        setLoading(false);

        const serviceLineNumber = detail.terminal.serviceLineNumber;

        // Telemetry+alerts and usage are independent, so they run together
        // rather than one after the other, and fail independently: a Clarus
        // outage should still leave the stored record on screen.
        const live = (async () => {
          try {
            const [t, a] = await Promise.all([
              getTerminalTelemetry(deviceId, signal),
              getTerminalAlerts(deviceId, signal),
            ]);
            if (signal?.aborted) return;
            setTelemetry(t.telemetry);
            setAlerts(a.devices || []);
          } catch (err: any) {
            if (!signal?.aborted) setLiveError(err?.message || 'Live telemetry is unavailable');
          } finally {
            if (!signal?.aborted) setLiveLoading(false);
          }
        })();

        const usageLoad = (async () => {
          if (!serviceLineNumber) {
            if (!signal?.aborted) setUsageLoading(false);
            return;
          }
          try {
            const u = await getTerminalUsage(serviceLineNumber, undefined, signal);
            if (signal?.aborted) return;
            setUsage(u.usage);

            // A billing cycle that has just started cannot fill a 30-day
            // window, so the previous period is pulled in as well. Mid-month
            // the current cycle already covers it and this is skipped.
            const currentDays = dailyUsageOf(u.usage);
            const prevPeriod = previousBillingPeriod(u.usage?.billingPeriod);
            if (needsPreviousCycle(currentDays) && prevPeriod) {
              try {
                const p = await getTerminalUsage(serviceLineNumber, prevPeriod, signal);
                if (!signal?.aborted) setPreviousDays(dailyUsageOf(p.usage));
              } catch {
                // A missing previous period just shortens the window.
                if (!signal?.aborted) setPreviousDays([]);
              }
            } else if (!signal?.aborted) {
              setPreviousDays([]);
            }
          } catch (err: any) {
            if (!signal?.aborted) setUsageError(err?.message || 'Usage data is unavailable');
          } finally {
            if (!signal?.aborted) setUsageLoading(false);
          }
        })();

        await Promise.all([live, usageLoad]);
      } catch (err: any) {
        if (!signal?.aborted) {
          setError(err?.message || 'Failed to load terminal');
          setLiveLoading(false);
          setUsageLoading(false);
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [deviceId]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // The chart follows the selected window; the allowance figures above it
  // always describe the current billing cycle, because a rolling window has no
  // allowance to measure against.
  const chartDays = useMemo(() => {
    const current = dailyUsageOf(usage);
    return usageWindow === 'cycle' ? current : lastNDays(previousDays, current);
  }, [usage, previousDays, usageWindow]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    setRefreshToken(t => t + 1);
    load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !terminal) {
    return (
      <div className="page-wrapper" style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/sims?type=starlink')}>
          Back to inventory
        </Button>
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message="Could not load this terminal"
          description={error || 'Terminal not found.'}
          action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  // Prefer the live reading when we have one; fall back to the synced snapshot.
  const live = telemetry;
  const activeAlerts = alerts.flatMap(d => d.alerts || []);
  const softwareUpdatePending = alerts.some(d => d.softwareUpdatePending);

  const usagePercent =
    usage && usage.subscriptionAllowance
      ? Math.min(100, Math.round(((usage.totalUsage || 0) / usage.subscriptionAllowance) * 100))
      : null;

  return (
    <div className="page-wrapper" style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
        <Col>
          <Space wrap>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/admin/sims?type=starlink')}
            >
              Back
            </Button>
            <h2 style={{ margin: 0 }}>{terminal.siteName || terminal.deviceId}</h2>
            <Badge
              status={terminal.isOnline ? 'success' : 'default'}
              text={terminal.isOnline ? 'Online' : 'Offline'}
            />
            {!terminal.isActive && <Tag color="red">Deactivated</Tag>}
          </Space>
        </Col>
        <Col>
          <Space>
            <AutoRefreshControl
              onRefresh={handleRefresh}
              loading={refreshing}
              intervalMs={autoRefreshMs}
              onIntervalChange={setAutoRefreshMs}
            />
            <PageHelp
              title="Starlink Terminal Help"
              sections={[
                {
                  title: 'Where this data comes from',
                  content:
                    'Identity, serials, site and online state come from our database, refreshed hourly from Clarus.\n\nLive telemetry, active alerts and data usage are fetched from Clarus when you open this page — so they are current, and they can be briefly unavailable if Clarus is down. The stored record stays visible either way.',
                },
                {
                  title: 'Metrics',
                  content:
                    '- Signal quality: 0–100%, higher is better\n- Ping latency: round-trip time in milliseconds\n- Ping drop rate: share of lost pings\n- Obstruction: share of time the dish view was blocked\n- Uptime: time since the terminal last restarted',
                },
                {
                  title: 'Read-only',
                  content:
                    'The Clarus API is read-only, so terminals cannot be suspended, resumed or terminated from the portal. Allocation and assignment are managed from the inventory.',
                },
              ]}
            />
          </Space>
        </Col>
      </Row>

      {activeAlerts.length > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message={`${activeAlerts.length} active alert${activeAlerts.length > 1 ? 's' : ''}`}
          description={
            <Space wrap>
              {activeAlerts.map(a => (
                <Tag color="orange" key={`${a.code}-${a.label}`}>
                  {a.label} ({a.code})
                </Tag>
              ))}
              {softwareUpdatePending && <Tag color="blue">Software update pending</Tag>}
            </Space>
          }
        />
      )}

      {liveError && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="Live telemetry unavailable"
          description={`${liveError} Showing the last synced reading instead.`}
        />
      )}

      {/* Headline metrics — live where available, synced otherwise. */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="Signal Quality"
              value={
                (live?.signalQuality ?? terminal.signalQuality)
                  ? fmtPercent(live?.signalQuality ?? terminal.signalQuality, 1)
                  : '—'
              }
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="Ping Latency"
              value={
                (live?.pingLatencyMsAvg ?? terminal.pingLatencyMsAvg) === null
                  ? '—'
                  : `${live?.pingLatencyMsAvg ?? terminal.pingLatencyMsAvg} ms`
              }
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="Download"
              value={fmtThroughput(live?.downlinkThroughput ?? terminal.downlinkThroughput)}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic title="Uptime" value={fmtUptime(live?.uptime ?? terminal.uptimeSeconds)} />
          </Card>
        </Col>
      </Row>

      {/* KAN-111: per-minute throughput for the previous complete hour. */}
      <div style={{ marginBottom: 16 }}>
        <TelemetryHistoryChart deviceId={terminal.deviceId} refreshToken={refreshToken} />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Terminal" size="small">
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Device ID">{terminal.deviceId}</Descriptions.Item>
              <Descriptions.Item label="Kit Serial">
                {terminal.kitSerialNumber || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Dish Serial">
                {terminal.dishSerialNumber || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Software">
                {live?.runningSoftwareVersion || terminal.runningSoftwareVersion || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Client">{terminal.clientName || '—'}</Descriptions.Item>
              <Descriptions.Item label="Last Reading">
                {fmtDate(live?.timestamp || terminal.lastTelemetryAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Synced">
                {fmtDate(terminal.lastSyncedAt)}
              </Descriptions.Item>
              {terminal.deactivatedAt && (
                <Descriptions.Item label="Deactivated">
                  {fmtDate(terminal.deactivatedAt)}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Allocation" size="small">
            {terminal.allocation ? (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Organization">
                  {terminal.allocation.orgName || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Vessel / Asset">
                  {terminal.allocation.assetId ? (
                    <Link to={`/admin/assets/${terminal.allocation.assetId}`}>
                      {terminal.allocation.assetName}
                    </Link>
                  ) : (
                    '—'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Assigned User">
                  {terminal.allocation.userId ? (
                    <Link to={`/admin/users/${terminal.allocation.userId}`}>
                      {terminal.allocation.userName}
                    </Link>
                  ) : (
                    '—'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Friendly Name">
                  {terminal.allocation.friendlyName || '—'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Not allocated to any organization"
              >
                <Link to="/admin/sims?type=starlink">
                  <Button type="primary">Allocate in inventory</Button>
                </Link>
              </Empty>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Service Line" size="small">
            {serviceLine ? (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Number">
                  {serviceLine.serviceLineNumber}
                </Descriptions.Item>
                <Descriptions.Item label="Site">{serviceLine.siteName || '—'}</Descriptions.Item>
                <Descriptions.Item label="Nickname">
                  {serviceLine.nickname || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {serviceLine.isActive === null ? (
                    '—'
                  ) : (
                    <Tag color={serviceLine.isActive ? 'green' : 'red'}>
                      {serviceLine.isActive ? 'Active' : 'Inactive'}
                    </Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Plan">
                  {serviceLine.subscription.description || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Activated">
                  {fmtDate(serviceLine.subscription.activationDate)}
                </Descriptions.Item>
                <Descriptions.Item label="Location">
                  {serviceLine.location.formattedAddress || '—'}
                </Descriptions.Item>
                {serviceLine.location.latitude !== null &&
                  serviceLine.location.longitude !== null && (
                    <Descriptions.Item label="Coordinates">
                      <a
                        href={`https://www.openstreetmap.org/?mlat=${serviceLine.location.latitude}&mlon=${serviceLine.location.longitude}#map=12/${serviceLine.location.latitude}/${serviceLine.location.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {serviceLine.location.latitude}, {serviceLine.location.longitude}
                      </a>
                    </Descriptions.Item>
                  )}
              </Descriptions>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No service line data synced yet"
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Connection Quality" size="small">
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Signal Quality">
                {fmtPercent(live?.signalQuality ?? terminal.signalQuality, 1)}
              </Descriptions.Item>
              <Descriptions.Item label="Ping Drop Rate">
                {fmtPercent(live?.pingDropRateAvg ?? terminal.pingDropRateAvg, 3)}
              </Descriptions.Item>
              <Descriptions.Item label="Obstruction">
                {fmtPercent(live?.obstructionPercentTime ?? terminal.obstructionPercentTime, 4)}
              </Descriptions.Item>
              <Descriptions.Item label="Download">
                {fmtThroughput(live?.downlinkThroughput ?? terminal.downlinkThroughput)}
              </Descriptions.Item>
              <Descriptions.Item label="Upload">
                {fmtThroughput(live?.uplinkThroughput ?? terminal.uplinkThroughput)}
              </Descriptions.Item>
              <Descriptions.Item label="Active Alerts">
                {activeAlerts.length === 0 ? (
                  <Tag color="green">None</Tag>
                ) : (
                  <Space wrap>
                    {activeAlerts.map(a => (
                      <Tag color="orange" key={`${a.code}-${a.label}`}>
                        {a.label}
                      </Tag>
                    ))}
                  </Space>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24}>
          {/*
            KAN-105: usage is reported per service line, not per terminal, and
            most service lines carry two dishes. Without this note the figure
            reads as "this terminal used 991 GB", which is wrong whenever the
            line is shared.
          */}
          <Card
            title={
              <Space size={8} wrap>
                <span>
                  Data Usage — {usageWindow === 'cycle' ? 'Current Billing Period' : 'Last 30 Days'}
                </span>
                {serviceLine && (
                  <Tag color="default">service line {serviceLine.serviceLineNumber}</Tag>
                )}
              </Space>
            }
            size="small"
          >
            {usageError ? (
              <Alert
                type="warning"
                showIcon
                message="Usage data unavailable"
                description={usageError}
              />
            ) : !usage ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No usage data" />
            ) : (
              <>
                <Alert
                  style={{ marginBottom: 16 }}
                  type="info"
                  showIcon
                  message="These figures cover the whole service line, not this terminal alone."
                  description="Clarus bills and reports data usage per service line. Where a service line carries more than one terminal — most vessels with two dishes — the usage below is their combined total and cannot be split per terminal."
                />
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={12} md={6}>
                    <Statistic title="Used" value={fmtGB(usage.totalUsage)} />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic
                      title="Remaining"
                      value={hasAllowance(usage) ? fmtGB(usage.remainingAllowance) : 'No allowance'}
                      valueStyle={hasAllowance(usage) ? undefined : { fontSize: 20 }}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title="Avg / day" value={fmtGB(usage.avgDailyUsage)} />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic
                      title="Forecast"
                      value={fmtGB(usage.forecastUsage)}
                      valueStyle={
                        usage.forecastToExceedAllowance ? { color: '#cf1322' } : undefined
                      }
                    />
                  </Col>
                </Row>

                {!hasAllowance(usage) && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {isOverageBilled(usage)
                      ? 'This plan has no included allowance — usage is billed as overage.'
                      : 'Clarus reports no allowance for this service line, so there is none to have remaining.'}
                  </Typography.Text>
                )}

                {usagePercent !== null && (
                  <Progress
                    percent={usagePercent}
                    status={usage.forecastToExceedAllowance ? 'exception' : 'normal'}
                    format={p => `${p}% of ${fmtGB(usage.subscriptionAllowance)}`}
                  />
                )}

                {usage.forecastToExceedAllowance && (
                  <Alert
                    style={{ marginTop: 12 }}
                    type="warning"
                    showIcon
                    message="Forecast to exceed allowance this period"
                  />
                )}

                <Descriptions
                  column={{ xs: 1, sm: 2, md: 3 }}
                  size="small"
                  style={{ marginTop: 16 }}
                >
                  <Descriptions.Item label="Plan">
                    {usage.subscriptionPackageName || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Period">
                    {usage.startDate} → {usage.endDate}
                  </Descriptions.Item>
                  <Descriptions.Item label="Days remaining">
                    {usage.remainingDays}
                  </Descriptions.Item>
                </Descriptions>

                {usage.billingCycles?.[0]?.dailyDataUsage?.length > 0 && (
                  <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Segmented
                      size="small"
                      value={usageWindow}
                      onChange={v => setUsageWindow(v as UsageWindow)}
                      options={[
                        { label: 'Last 30 days', value: 'last30' },
                        { label: 'This billing period', value: 'cycle' },
                      ]}
                    />
                  </div>
                )}
                {usage.billingCycles?.[0]?.dailyDataUsage?.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <UsageChart days={chartDays} />
                    {usageWindow === 'last30' && (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Chart and table show the last 30 days. Used, remaining and forecast above
                        are for the current billing period.
                      </Typography.Text>
                    )}
                  </div>
                )}
                {usage.billingCycles?.[0]?.dailyDataUsage?.length > 0 && (
                  <Table
                    style={{ marginTop: 16 }}
                    size="small"
                    rowKey="date"
                    dataSource={[...chartDays].reverse()}
                    pagination={{ pageSize: 10, size: 'small' }}
                    scroll={{ x: true }}
                    columns={[
                      {
                        title: 'Date',
                        dataIndex: 'date',
                        key: 'date',
                        render: (d: string) => fmtDay(d),
                      },
                      {
                        title: 'Priority',
                        dataIndex: 'priorityGB',
                        key: 'priorityGB',
                        render: (v: number) => fmtGB(v),
                      },
                      {
                        title: 'Standard',
                        dataIndex: 'standardGB',
                        key: 'standardGB',
                        render: (v: number) => fmtGB(v),
                      },
                      {
                        title: 'Non-billable',
                        dataIndex: 'nonBillableGB',
                        key: 'nonBillableGB',
                        render: (v: number) => fmtGB(v),
                      },
                    ]}
                  />
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TerminalDetail;
